import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'
import Swal from 'sweetalert2'
import {
  genWin2KeepOrderNoDouble,
  genWin2KeepOrderIncludeDouble,
  genWin3KeepOrderNoHamNoTong,
  genWin3KeepOrderIncludeHamTong,
} from '../../utils/winNumberUtils'
import { numberSetService, type NumberSetItem } from '../../services/numberSetService'
import { CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES, formatCustomerSlipCancelWindowText, lotteryService } from '../../services/lotteryService'
import RulesGuideModal from '../../components/RulesGuideModal'
import type { LobbyHuayOutletContext } from './ModernLobbyHuayLayout'

/** Map typeLabel (จับวิน) → prizeTypeCode (API เลขชุด) — ตรงกับ user-classic NumberSetCreatePage */
const TYPE_LABEL_TO_PRIZE_CODE: Record<string, string> = {
  '2 ตัวบน': '2_DIGIT_TOP',
  '3 ตัวบน': '3_DIGIT_TOP',
  '3 ตัวโต๊ด': '3_DIGIT_TOD',
  '2 ตัวโต๊ด': '2_DIGIT_TOD',
  '2 ตัวล่าง': '2_DIGIT_BOTTOM',
  '3 ตัวล่าง': '3_DIGIT_BOTTOM',
}

type WinResultItem = { id: string; type: string; number: string }

/** Payload ส่งไปหน้า bet เพื่อนำเลขวินไปใส่ตารางแทง */
export interface FromWinNumberItem {
  type: string
  number: string
}

/** หน้า จับวินเลข — ยึดลำดับตัวเลขที่เลือก (ไม่ sort) */
const ModernLobbyHuayWinNumberPage: React.FC = () => {
  useOutletContext<LobbyHuayOutletContext>()
  const navigate = useNavigate()

  const [setName, setSetName] = useState('')
  const setNameInputRef = useRef<HTMLInputElement>(null)
  const [selectedDigits, setSelectedDigits] = useState<number[]>([])
  const [results, setResults] = useState<WinResultItem[]>([])
  const [rulesModalOpen, setRulesModalOpen] = useState(false)
  const [cancelWindowMinutes, setCancelWindowMinutes] = useState(CUSTOMER_SLIP_CANCEL_WINDOW_MINUTES)

  useEffect(() => {
    let isMounted = true
    void lotteryService.getCustomerSlipCancelWindowMinutes().then((minutes) => {
      if (isMounted) setCancelWindowMinutes(minutes)
    })
    return () => {
      isMounted = false
    }
  }, [])
  const [saving, setSaving] = useState(false)
  /** จับวิน 2 ตัว: เลือกได้อย่างใดอย่างหนึ่ง (ไม่รวมเบิ้ล หรือ รวมเบิ้ล) */
  const [selectedWin2, setSelectedWin2] = useState<'no-double' | 'with-double' | null>(null)
  /** จับวิน 3 ตัว: เลือกได้อย่างใดอย่างหนึ่ง (ไม่รวมหามตอง หรือ รวมหามตอง) */
  const [selectedWin3, setSelectedWin3] = useState<'no-triple' | 'with-triple' | null>(null)

  const digits = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0]

  const toggleDigit = useCallback((d: number) => {
    setSelectedDigits((prev) => {
      const i = prev.indexOf(d)
      if (i >= 0) return prev.filter((_, idx) => idx !== i)
      return [...prev, d]
    })
  }, [])

  const selectedDisplay = useMemo(
    () => selectedDigits.join(','),
    [selectedDigits]
  )

  const canGenerate = selectedDigits.length >= 3

  const addResults = useCallback((typeLabel: string, numbers: string[]) => {
    const newItems: WinResultItem[] = numbers.map((number, i) => ({
      id: `win-${Date.now()}-${i}-${number}`,
      type: typeLabel,
      number,
    }))
    setResults((prev) => [...prev, ...newItems])
  }, [])

  /** ปุ่ม 4 อัน: เลือก/ยกเลิกเท่านั้น (กดซ้ำ = ลบออก) — ยังไม่สร้างเลขจนกว่ากด "จับวินตัวเลข" */
  const handleWin2NoDouble = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin2((prev) => (prev === 'no-double' ? null : 'no-double'))
  }, [canGenerate])

  const handleWin2WithDouble = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin2((prev) => (prev === 'with-double' ? null : 'with-double'))
  }, [canGenerate])

  const handleWin3NoTriple = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin3((prev) => (prev === 'no-triple' ? null : 'no-triple'))
  }, [canGenerate])

  const handleWin3WithTriple = useCallback(() => {
    if (!canGenerate) return
    setSelectedWin3((prev) => (prev === 'with-triple' ? null : 'with-triple'))
  }, [canGenerate])

  /** ลำดับแสดงประเภทในรายการทั้งหมด (ครบ 6 ประเภท) */
  const PRIZE_TYPE_ORDER = [
    '2 ตัวบน',
    '3 ตัวบน',
    '3 ตัวโต๊ด',
    '2 ตัวโต๊ด',
    '2 ตัวล่าง',
    '3 ตัวล่าง',
  ] as const

  /** กด "จับวินตัวเลข" ถึงจะสร้างเลขวิน — ผลลัพธ์กระจายเข้า 6 ประเภท (2 ตัวบน, 3 ตัวบน, 3 ตัวโต๊ด, 2 ตัวโต๊ด, 2 ตัวล่าง, 3 ตัวล่าง) */
  const hasWinSelection = selectedWin2 != null || selectedWin3 != null
  const handleDrawWinNumbers = useCallback(() => {
    if (!canGenerate || !hasWinSelection) return
    try {
      if (selectedWin2 === 'no-double') {
        const numbers = genWin2KeepOrderNoDouble(selectedDigits)
        addResults('2 ตัวบน', numbers)
        addResults('2 ตัวโต๊ด', numbers)
        addResults('2 ตัวล่าง', numbers)
      }
      if (selectedWin2 === 'with-double') {
        const numbers = genWin2KeepOrderIncludeDouble(selectedDigits)
        addResults('2 ตัวบน', numbers)
        addResults('2 ตัวโต๊ด', numbers)
        addResults('2 ตัวล่าง', numbers)
      }
      if (selectedWin3 === 'no-triple') {
        const numbers = genWin3KeepOrderNoHamNoTong(selectedDigits)
        addResults('3 ตัวบน', numbers)
        addResults('3 ตัวโต๊ด', numbers)
        addResults('3 ตัวล่าง', numbers)
      }
      if (selectedWin3 === 'with-triple') {
        const numbers = genWin3KeepOrderIncludeHamTong(selectedDigits)
        addResults('3 ตัวบน', numbers)
        addResults('3 ตัวโต๊ด', numbers)
        addResults('3 ตัวล่าง', numbers)
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      window.alert(msg)
    }
  }, [canGenerate, hasWinSelection, selectedWin2, selectedWin3, selectedDigits, addResults])

  const removeResult = useCallback((id: string) => {
    setResults((prev) => prev.filter((r) => r.id !== id))
  }, [])

  /** จัดกลุ่มตามประเภท และเรียงตาม PRIZE_TYPE_ORDER (แสดงครบ 6 ประเภท) */
  const groupByType = useMemo(() => {
    const map = new Map<string, WinResultItem[]>()
    results.forEach((r) => {
      const list = map.get(r.type) ?? []
      list.push(r)
      map.set(r.type, list)
    })
    return PRIZE_TYPE_ORDER.map((typeLabel) => ({
      typeLabel,
      items: map.get(typeLabel) ?? [],
    }))
  }, [results])

  const deleteByType = useCallback((typeLabel: string) => {
    setResults((prev) => prev.filter((r) => r.type !== typeLabel))
  }, [])

  /** ล้างทั้งหมดเหมือนเข้าใหม่: ตาราง, เลขที่เลือก, จับวิน 2/3, ชื่อชุด */
  const clearAll = useCallback(() => {
    setResults([])
    setSelectedDigits([])
    setSelectedWin2(null)
    setSelectedWin3(null)
    setSetName('')
  }, [])

  /** บันทึกข้อมูล: บังคับชื่อชุด + มีรายการจับวิน แล้วเรียก API สร้างเลขชุด (เดียวกับ user-classic) */
  const handleSaveData = useCallback(async () => {
    const name = setName.trim()
    if (!name) {
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'warning',
        title: 'กรุณาระบุชื่อเลขชุด',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
      setTimeout(() => setNameInputRef.current?.focus(), 100)
      return
    }
    if (results.length === 0) {
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'warning',
        title: 'กรุณาจับวินตัวเลขก่อน',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
      return
    }
    const items: NumberSetItem[] = groupByType
      .filter((g) => g.items.length > 0)
      .map((g) => ({
        prizeTypeCode: TYPE_LABEL_TO_PRIZE_CODE[g.typeLabel] ?? g.typeLabel,
        typeLabel: g.typeLabel,
        numbers: g.items.map((i) => i.number).join(', '),
      }))
    if (items.length === 0) return
    try {
      setSaving(true)
      await numberSetService.create({ name, items })
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'success',
        title: 'บันทึกเลขชุดเรียบร้อยแล้ว',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      })
      clearAll()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'บันทึกเลขชุดไม่สำเร็จ'
      Swal.fire({
        toast: true,
        position: 'top',
        icon: 'error',
        title: msg,
        showConfirmButton: false,
        timer: 4000,
        timerProgressBar: true,
      })
    } finally {
      setSaving(false)
    }
  }, [setName, results.length, groupByType, clearAll])

  return (
    <>
      <div className="c_quinary text-center position-relative">
        <Link
          to="/lobby-huay/set"
          className="link_back"
        >
          <i className="fas fa-chevron-left me-1" /> กลับหน้าหลัก
        </Link>
        <button
          type="button"
          className="bg_gd_quaternary hh_hover border_5 px-2 py-1 ms-auto"
          style={{ width: 'fit-content' }}
          onClick={() => setRulesModalOpen(true)}
        >
          <h6 className="c_tertiary mb-0">กติกา & วิธีดูผลรางวัล</h6>
        </button>
      </div>

      <div className="row mx__1 mt-3" style={{ rowGap: '1rem' }}>
        <div className="col-12 col-md-6 px-1">
          <div className="d-grid gap-2">
            <div className="h_box p-0">
              <div className="d-flex align-items-center c_tertiary">
                <h5><i className="fas fa-list" /></h5>
                <h6 className="font_md ms-2">เลขชุด</h6>
              </div>
            </div>

            <div className="border_5 bdc_primary bg_op_secondary p-2 overflow-hidden">
              <div className="c_tertiary mb-2">
                <h6 className="font_md">ระบุชื่อเรียกเลขชุด</h6>
              </div>
              <div className="bg_secondary box_row flex-nowrap border_5 px-2 py-1 w-100">
                <h5 className="mb-0"><i className="fas fa-keyboard" /></h5>
                <input
                  ref={setNameInputRef}
                  type="text"
                  placeholder="ชื่อเลขชุด"
                  className="w-100 border-0 px-2 bg-transparent c_secondary"
                  value={setName}
                  onChange={(e) => setSetName(e.target.value)}
                />
              </div>
            </div>

            <div className="light_sd border_5 d-grid gap-2 bdc_primary bg_op_secondary p-2 overflow-hidden">
              <h6 className="bg_primary c_tertiary rounded-1 text-center px-2 mx-auto" style={{ width: 'fit-content' }}>
                รายการที่เลือก
              </h6>
              <h1 className="c_tertiary text-center mb-0" style={{ fontSize: '1.5rem' }}>
                {selectedDisplay || '-'}
              </h1>
            </div>

            <div className="border_5 d-grid gap-2 bdc_primary bg_op_secondary p-2 overflow-hidden">
              <h6 className="c_tertiary text-center mb-0">
                กรุณา เลือก ตัวเลขที่ต้องการจับวิน อย่างน้อย 3 ตัวขึ้นไป
              </h6>
              <div className="select_number d-flex flex-wrap gap-1 justify-content-center">
                {digits.map((d) => {
                  const isActive = selectedDigits.includes(d)
                  return (
                    <div
                      key={d}
                      role="button"
                      tabIndex={0}
                      className={`hh_hover bg_secondary bdc_primary ins_sd border_5 py-1 h_num ${isActive ? 'hh_active' : ''}`}
                      style={{ minWidth: 44 }}
                      onClick={() => toggleDigit(d)}
                      onKeyDown={(e) => e.key === 'Enter' && toggleDigit(d)}
                    >
                      <h4 className="c_primary font_md text-center mb-0">{d}</h4>
                    </div>
                  )
                })}
              </div>

              <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
                {/* จับวิน 2 ตัว: เลือกได้อย่างใดอย่างหนึ่ง (ไม่รวมเบิ้ล หรือ รวมเบิ้ล) */}
                <div className="col-12 col-lg-6 px-1">
                  <div
                    role="button"
                    tabIndex={0}
                    className={`bg_quaternary hh_hover text-center border_5 py-2 ${selectedWin2 === 'no-double' ? 'select_win' : ''}`}
                    onClick={handleWin2NoDouble}
                    onKeyDown={(e) => e.key === 'Enter' && handleWin2NoDouble()}
                  >
                    <h6 className="c_tertiary mb-0">จับวิน 2 ตัวไม่รวมเลขเบิ้ล</h6>
                  </div>
                </div>
                <div className="col-12 col-lg-6 px-1">
                  <div
                    role="button"
                    tabIndex={0}
                    className={`bg_quaternary hh_hover text-center border_5 py-2 ${selectedWin2 === 'with-double' ? 'select_win' : ''}`}
                    onClick={handleWin2WithDouble}
                    onKeyDown={(e) => e.key === 'Enter' && handleWin2WithDouble()}
                  >
                    <h6 className="c_tertiary mb-0">จับวิน 2 ตัวรวมเลขเบิ้ล</h6>
                  </div>
                </div>
                {/* จับวิน 3 ตัว: เลือกได้อย่างใดอย่างหนึ่ง (ไม่รวมหามตอง หรือ รวมหามตอง) */}
                <div className="col-12 col-lg-6 px-1">
                  <div
                    role="button"
                    tabIndex={0}
                    className={`bg_quaternary hh_hover text-center border_5 py-2 ${selectedWin3 === 'no-triple' ? 'select_win' : ''}`}
                    onClick={handleWin3NoTriple}
                    onKeyDown={(e) => e.key === 'Enter' && handleWin3NoTriple()}
                  >
                    <h6 className="c_tertiary mb-0">จับวิน 3 ตัวไม่รวมเลขหามตอง</h6>
                  </div>
                </div>
                <div className="col-12 col-lg-6 px-1">
                  <div
                    role="button"
                    tabIndex={0}
                    className={`bg_quaternary hh_hover text-center border_5 py-2 ${selectedWin3 === 'with-triple' ? 'select_win' : ''}`}
                    onClick={handleWin3WithTriple}
                    onKeyDown={(e) => e.key === 'Enter' && handleWin3WithTriple()}
                  >
                    <h6 className="c_tertiary mb-0">จับวิน 3 ตัวรวมเลขหามตอง</h6>
                  </div>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_5 w-100 border-0"
              disabled={!canGenerate || !hasWinSelection}
              onClick={handleDrawWinNumbers}
            >
              <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                <span>จับวินตัวเลข</span>
              </div>
            </button>
          </div>
        </div>

        <div className="col-12 col-md-6 px-1">
          <div className="d-grid gap-2">
            <div className="h_box p-0">
              <div className="d-flex align-items-center c_tertiary">
                <h5><i className="fas fa-list" /></h5>
                <h6 className="font_md ms-2">รายการทั้งหมด</h6>
              </div>
            </div>

            {results.length === 0 ? (
              <div className="border_5 bg_op_secondary p-3 text-center">
                <h6 className="c_tertiary mb-0">ยังไม่มีรายการจับวิน เลือกตัวเลขแล้วกดปุ่มจับวินด้านซ้าย</h6>
              </div>
            ) : (
              groupByType.map(({ typeLabel, items }) => (
              <div key={typeLabel}>
                <div className="bg_op_secondary p-1 border_5">
                  <div className="row mx__1 align-items-center" style={{ rowGap: '0.5rem' }}>
                    <div className="col-4 px-1">
                      <h5 className="c_tertiary font_md mb-0">{typeLabel}</h5>
                    </div>
                    <div className="col-8 px-1">
                      <button
                        type="button"
                        className="bg_primary hh_hover bdc_primary border_5 py-1 px-2 ms-auto"
                        style={{ width: 'fit-content' }}
                        disabled={items.length === 0}
                        onClick={() => deleteByType(typeLabel)}
                      >
                        <span className="c_tertiary">
                          <i className="fas fa-trash-alt me-1" />
                          ลบชนิดนี้ทั้งหมด
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
                <div className="light_sd border_5 overflow-hidden">
                  <table className="table bg_secondary table-striped table-huay-result text-center border_5 overflow-hidden mb-0">
                    <thead className="bg_gd_primary">
                      <tr>
                        <th scope="col" className="c_tertiary bg-transparent" style={{ width: '15%' }}>##</th>
                        <th scope="col" className="c_tertiary bg-transparent" style={{ width: '35%' }}>
                          <div className="bg_op_secondary bdc_primary border_5 c_tertiary py-1">ชนิด</div>
                        </th>
                        <th scope="col" className="c_tertiary bg-transparent" style={{ width: '35%' }}>ตัวเลข</th>
                        <th scope="col" className="c_tertiary bg-transparent" style={{ width: '15%' }}>ลบ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="c_secondary py-2">ไม่มีรายการ</td>
                        </tr>
                      ) : (
                        items.map((row, idx) => (
                          <tr key={row.id}>
                            <th scope="row" className="c_secondary">{idx + 1}</th>
                            <td>
                              <div className="bg_gd_secondary border_5 c_tertiary font_md py-1">{row.type}</div>
                            </td>
                            <td><div className="c_primary">{row.number}</div></td>
                            <td>
                              <button
                                type="button"
                                className="bg_primary hh_hover bdc_primary border_5 py-1 border-0"
                                onClick={() => removeResult(row.id)}
                              >
                                <span className="c_tertiary"><i className="fas fa-trash-alt" /></span>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
            )}

            <div className="row mx__1" style={{ rowGap: '0.5rem' }}>
              <div className="col-12 px-1">
                <Link
                  to="/lobby-huay/set"
                  className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_5 text-decoration-none w-100"
                >
                  <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                    <i className="fas fa-plus-square me-2" />
                    <span>เพิ่มชุดตัวเลข</span>
                  </div>
                </Link>
              </div>
              <div className="col-12 px-1">
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bdc_primary bg_secondary border_5 w-100 border-0"
                  onClick={clearAll}
                >
                  <div className="d-flex align-items-center c_primary font_md">
                    <i className="fas fa-minus-square me-2" />
                    <span>ล้างเลขจับวินทั้งหมด</span>
                  </div>
                </button>
              </div>
              <div className="col-12 px-1">
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bdc_primary bg_gd_primary border_5 w-100 border-0"
                  disabled={results.length === 0}
                  onClick={() => {
                    const payload: FromWinNumberItem[] = results.map((r) => ({ type: r.type, number: r.number }))
                    navigate('/lobby-huay/listall', { state: { fromWinNumber: payload } })
                  }}
                >
                  <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                    <i className="fas fa-pencil-alt me-2" />
                    <span>แทงหวยเลย</span>
                  </div>
                </button>
              </div>
              <div className="col-6 px-1">
                <button
                  type="button"
                  className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_5 w-100 border-0"
                  disabled={saving || results.length === 0}
                  onClick={handleSaveData}
                >
                  <div className="d-flex align-items-center ft_sd c_tertiary font_md">
                    {saving ? (
                      <i className="fas fa-spinner fa-spin me-2" />
                    ) : (
                      <i className="fas fa-check-circle me-2" />
                    )}
                    <span>{saving ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}</span>
                  </div>
                </button>
              </div>
              <div className="col-6 px-1">
                <Link
                  to="/lobby-huay/set"
                  className="link_pmr box_row justify-content-center font_rg bdc_primary bg_secondary border_5 text-decoration-none w-100"
                >
                  <div className="d-flex align-items-center c_primary font_md">
                    <i className="fas fa-times-circle me-2" />
                    <span>ลบเลขชุดทิ้ง</span>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RulesGuideModal
        isOpen={rulesModalOpen}
        onClose={() => setRulesModalOpen(false)}
        fallbackRules={(
          <>
            <p>- ระบบเปิด-ปิดแทงตามเวลาที่ได้แจ้งไว้ และบริษัทสามารถพิจารณาปิดแทงก่อนกำหนดได้</p>
            <p>- ทางบริษัทสามารถคืนเลขได้ทุกกรณี แต่จะคืนก่อนที่จะออกผลรางวัล</p>
            <p>- การคืนเลขหรือคืนโพยของสมาชิก ต้องเป็นไปเงื่อนไขและเวลาที่ทางเว็บกำหนดเท่านั้น (<span className="c_primary"><strong>คืนโพยได้ภายใน {formatCustomerSlipCancelWindowText(cancelWindowMinutes)}หลังจากที่แทง</strong></span>)</p>
            <p>- หากหวยนั้นๆ มีการออกผลรางวัลก่อนที่จะปิดแทง บริษัทจะทำการยกเลิกรายการที่แทงเฉพาะบางเลขที่ได้ทำการแทงหลังจากหวยนั้นๆ ออกผล</p>
            <p>- ราคาจ่ายและส่วนลดจะลดลงตามเลขดังหรือเลขอั้น ให้สมาชิกตรวจสอบโพยและราคาจ่าย/ส่วนลดหลังการแทงทุกครั้ง</p>
            <p>- การสมัครสมาชิกหรือแนะนำชื่อตัวเองเพื่อปั่นค่าคอมมิชชั่น จะถูกพิจารณาปิดบัญชีและยึดเครดิตของบัญชีที่เกี่ยข้องทั้งหมด</p>
            <p>- สมาชิกที่จงใจเจตนาทุจริตในการเล่น จะถูกปิดบัญชีและไม่คืนเครดิตให้ทุกกรณี</p>
            <p>- กรณีที่เซิฟเวอร์หรืออินเตอร์เน็ตล่มในช่วงการเปิดรับแทง และเกิดข้อโต้แย้งเกี่ยวกับการออกผลรางวัล ทางบริษัทมีสิทธิ์จะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ผลรางวัลคลุมเครือหรือมีแนวโน้มที่ไม่โปร่งใส ทางบริษัทจะพิจารณายกเลิกรายการงวดและคืนเครดิตให้ลูกค้า</p>
            <p>- ในกรณีเกิดข้อพิพาทและโต้แย้งในการออกผลรางวัล การตัดสินของบริษัทถือเป็นข้อยุติและสิ้นสุด</p>
            <p>- <span className="c_primary">สมาชิกที่สมัครมากกว่า 1 ยูสเซอร์ โดยใช้ชื่อเดียวกัน เพื่อผลประโยชน์ในการแทงได้มากกว่าหรือปั่นค่าแนะนำ จะถูกระงับและไม่คืนเครดิตให้ทุกรณี</span></p>
          </>
        )}
        fallbackGuides={(
          <>
            <p className="mt-3"><u><strong>วิธีการดูผลรางวัล 'หวยรัฐบาลไทย'</strong></u></p>
            <div className="mt-3 mb-4 text-center">
              <p className="mb-1">ใช้รางวัลที่ 1 ในการออกผลรางวัล เช่น 199606</p>
              <p className="mb-1">สามตัวบน (606) สองตัวล่าง (94)</p>
              <p className="mb-1">สามตัวล่าง (220,173,388,094)</p>
            </div>
            <p><u><strong>การตรวจผลรางวัล</strong></u></p>
            <table className="table table-bordered mb-0">
              <tbody>
                <tr><td className="text-nowrap" style={{ width: 100 }}>3 ตัวบน</td><td>606</td></tr>
                <tr><td className="text-nowrap">2 ตัวบน</td><td>06</td></tr>
                <tr><td className="text-nowrap">2 ตัวโต๊ด</td><td>94 49 06 60</td></tr>
                <tr><td className="text-nowrap">3 ตัวโต๊ด</td><td>606 660 066</td></tr>
                <tr><td className="text-nowrap">วิ่งบน</td><td>6 - 0 - 6</td></tr>
                <tr><td className="text-nowrap">ปักหลักร้อย</td><td>6</td></tr>
                <tr><td className="text-nowrap">ปักหลักสิบ</td><td>0</td></tr>
                <tr><td className="text-nowrap">ปักหลักหน่วย</td><td>6</td></tr>
                <tr><td className="text-nowrap">2 ตัวล่าง</td><td>94</td></tr>
                <tr><td className="text-nowrap">วิ่งล่าง</td><td>4 - 9</td></tr>
                <tr><td className="text-nowrap">4 ตัวบน</td><td>9606</td></tr>
                <tr><td className="text-nowrap">4 ตัวโต๊ด</td><td>9606 สลับตำแหน่งทั้ง 4 หลัก</td></tr>
                <tr><td className="text-nowrap">5 ตัวโต๊ด</td><td>99606 สลับตำแหน่งทั้ง 5 หลัก</td></tr>
              </tbody>
            </table>
          </>
        )}
      />
    </>
  )
}

export default ModernLobbyHuayWinNumberPage
